import { Head } from 'vite-react-ssg';
import { useParams } from 'react-router-dom';
import { ChevronLeft, MapPin, Phone, Globe } from 'lucide-react';
import { Container, Eyebrow } from '../components/ui-bits';
import { getCategory, getListing } from '../directory/data';
import { SIGNUP_URL } from '../config';

/** /discover/:category/:slug — a single listing. */
export default function Place() {
  const { category = '', slug = '' } = useParams();
  const listing = getListing(category, slug);
  const cat = getCategory(category);

  if (!listing) {
    return (
      <main>
        <Head><title>Not found | TechSoFit</title></Head>
        <Container>
          <p className="py-24 text-center text-muted">This listing does not exist. <a className="text-ink underline" href="/discover">Back to Discover</a></p>
        </Container>
      </main>
    );
  }

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SportsActivityLocation',
    name: listing.name,
    description: listing.summary,
    address: {
      '@type': 'PostalAddress',
      addressLocality: listing.area,
      addressRegion: listing.city,
      addressCountry: 'ZW',
    },
  });

  return (
    <main>
      <Head>
        <title>{listing.name}: {cat?.label} in {listing.area}, {listing.city} | TechSoFit</title>
        <meta name="description" content={listing.summary} />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>

      <section className="py-16 md:py-20">
        <Container className="max-w-3xl">
          <a href={`/discover/${listing.category}`} className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink">
            <ChevronLeft className="size-4" /> {cat?.label}
          </a>
          <Eyebrow>{cat?.label}</Eyebrow>
          <h1 className="mt-3 font-display text-display-md text-ink">{listing.name}</h1>
          <p className="mt-3 flex items-center gap-1.5 text-ink-soft">
            <MapPin className="size-4" /> {listing.area}, {listing.city}
          </p>
          <p className="mt-5 text-lg text-ink-soft">{listing.summary}</p>

          {(listing.address || listing.phone || listing.website) && (
            <div className="mt-6 space-y-2 rounded-card bg-card p-5 shadow-card">
              {listing.address && (
                <p className="flex items-center gap-2 text-sm text-ink"><MapPin className="size-4 text-muted" /> {listing.address}</p>
              )}
              {listing.phone && (
                <p className="flex items-center gap-2 text-sm text-ink"><Phone className="size-4 text-muted" /> {listing.phone}</p>
              )}
              {listing.website && (
                <p className="flex items-center gap-2 text-sm text-ink"><Globe className="size-4 text-muted" /> {listing.website}</p>
              )}
            </div>
          )}

          <div className="mt-10 rounded-card bg-accent px-6 py-6 sm:px-8">
            <p className="font-display text-lg font-semibold text-accent-ink">Is this your business?</p>
            <p className="mt-1 text-sm text-accent-ink/80">Manage your listing, take payments and give members the app. Put your business on TechSoFit.</p>
            <a href={SIGNUP_URL} className="mt-4 inline-flex rounded-pill bg-dark px-5 py-2.5 text-sm font-medium text-on-dark transition-opacity hover:opacity-90">
              Get started
            </a>
          </div>
        </Container>
      </section>
    </main>
  );
}
